<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'ChatMateKnowledge' }" class="btn btn-secondary">
          <i class="ri-arrow-left-line"></i>
          返回列表
        </router-link>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="pendingCount === 0 || uploading"
          @click="handleUpload"
        >
          <span v-if="uploading">上傳中 {{ uploadedCount }}/{{ totalUploadCount }}...</span>
          <span v-else>上傳 {{ pendingCount > 0 ? `(${pendingCount} 個檔案)` : '' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <div class="upload-form">
      <form @submit.prevent="handleUpload">
        <div class="form-group">
          <label>檔案 <span class="required">*</span></label>
          <div
            class="file-upload-area"
            :class="{ 'dragover': isDragging }"
            @click="$refs.fileInput.click()"
            @drop.prevent="handleDrop"
            @dragover.prevent="isDragging = true"
            @dragenter.prevent="isDragging = true"
            @dragleave="isDragging = false"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,.docx,.txt,.md,.xlsx"
              multiple
              @change="handleFileSelect"
              style="display: none"
            />
            <div class="upload-placeholder">
              <i class="ri-upload-cloud-line"></i>
              <p>點擊或拖放檔案到此處</p>
              <p class="hint">支援 PDF、DOCX、XLSX、TXT、MD 格式，單檔最大 5MB，一次最多 30 個檔案</p>
            </div>
          </div>
          <button type="button" class="btn btn-secondary" @click.stop="$refs.fileInput.click()">
            選擇檔案
          </button>
        </div>

        <!-- 格式說明 -->
        <div class="format-tips">
          <div class="format-tips-title"><i class="ri-information-line"></i> 格式說明</div>
          <ul>
            <li><strong>PDF / DOCX</strong>：僅擷取純文字，圖片與掃描式文件無法解析</li>
            <li><strong>TXT / MD</strong>：直接讀取原始內容，建議使用 UTF-8 編碼</li>
            <li><strong>XLSX</strong>：每個 Sheet 會逐列轉為文字，<span class="tip-highlight">請確保第一列為欄位標題</span>，否則欄位名稱將顯示為 A、B、C</li>
          </ul>
        </div>

        <!-- 已選檔案列表 -->
        <div v-if="selectedFiles.length > 0" class="file-list">
          <div class="file-list-header">
            <span>
              共 {{ selectedFiles.length }} 個檔案
              <template v-if="pendingCount > 0">・待上傳 {{ pendingCount }} 個</template>
              <template v-if="failedCount > 0">・<span class="header-failed">失敗 {{ failedCount }} 個</span></template>
            </span>
            <div class="file-list-header-actions">
              <button
                v-if="failedCount > 0 && !uploading"
                type="button"
                class="btn-retry-all"
                @click="retryAllFailed"
              >
                <i class="ri-refresh-line"></i> 重試失敗項目
              </button>
              <button type="button" class="btn-clear-all" @click="clearAllFiles">全部清除</button>
            </div>
          </div>
          <div
            v-for="(item, index) in selectedFiles"
            :key="index"
            class="file-item"
            :class="item.status"
          >
            <i :class="getFileIcon(item.file.name)" class="file-type-icon"></i>
            <div class="file-item-info">
              <span class="file-name">{{ item.file.name }}</span>
              <span class="file-size">{{ formatFileSize(item.file.size) }}</span>
              <span v-if="item.status === 'error'" class="file-error-msg">{{ item.errorMessage }}</span>
            </div>
            <div class="file-item-status">
              <span v-if="item.status === 'pending'" class="status-badge pending">待上傳</span>
              <span v-else-if="item.status === 'uploading'" class="status-badge uploading">
                <i class="ri-loader-4-line spinning"></i> 上傳中
              </span>
              <span v-else-if="item.status === 'success'" class="status-badge success">
                <i class="ri-check-line"></i> 完成
              </span>
              <span v-else-if="item.status === 'error'" class="status-badge error">
                <i class="ri-error-warning-line"></i> 失敗
              </span>
            </div>
            <button
              v-if="item.status === 'error' && !uploading"
              type="button"
              class="btn-retry"
              title="重試"
              @click.stop="retryFile(index)"
            >
              <i class="ri-refresh-line"></i>
            </button>
            <button
              v-if="item.status === 'pending'"
              type="button"
              class="btn-remove"
              @click.stop="removeFile(index)"
            >
              <i class="ri-close-line"></i>
            </button>
          </div>
        </div>

        <div class="form-group">
          <label>標籤</label>
          <input
            v-model="tagsInput"
            type="text"
            class="form-control"
            placeholder="輸入標籤，以逗號分隔，套用至所有上傳檔案（選填）"
          />
        </div>
      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { chatmateService } from '@/api/modules/chatmate.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'ChatMateKnowledgeUpload',
  setup() {
    const { pageTitle } = usePageTitle('上傳知識庫');
    const router = useRouter();
    const fileInput = ref(null);
    const selectedFiles = ref([]);
    const isDragging = ref(false);
    const uploading = ref(false);
    const uploadedCount = ref(0);
    const totalUploadCount = ref(0);
    const tagsInput = ref('');

    const pendingCount = computed(() => selectedFiles.value.filter(i => i.status === 'pending').length);
    const failedCount = computed(() => selectedFiles.value.filter(i => i.status === 'error').length);

    const ALLOWED_TYPES = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/plain',
      'text/markdown',
      'text/x-markdown'
    ];
    // 副檔名優先判斷，避免 application/octet-stream 等非標準 MIME type 被誤拒
    const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.xlsx'];
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB
    const MAX_FILES = 30;

    const validateFile = (file) => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      // 副檔名優先判斷，MIME type 作為補充（避免 application/octet-stream 等非標準類型被誤拒）
      if (!ALLOWED_EXTENSIONS.includes(ext) && !ALLOWED_TYPES.includes(file.type)) {
        throw new Error(`不支援的檔案格式：${ext || file.type}`);
      }
      if (file.size > MAX_SIZE) {
        throw new Error(`${file.name} 超過 5MB 限制`);
      }
    };

    const addFiles = (files) => {
      const currentCount = selectedFiles.value.filter(i => i.status === 'pending').length;
      const remaining = MAX_FILES - currentCount;

      if (remaining <= 0) {
        showError('超出數量限制', `一次最多上傳 ${MAX_FILES} 個檔案`);
        return;
      }

      const errors = [];
      let added = 0;
      for (const file of files) {
        if (added >= remaining) {
          errors.push(`已達上限（${MAX_FILES} 個），其餘檔案略過`);
          break;
        }

        // 避免重複加入同名同大小的檔案
        const isDuplicate = selectedFiles.value.some(
          item => item.file.name === file.name && item.file.size === file.size
        );
        if (isDuplicate) continue;

        try {
          validateFile(file);
          selectedFiles.value.push({ file, status: 'pending', errorMessage: '' });
          added++;
        } catch (err) {
          errors.push(err.message);
        }
      }
      if (errors.length > 0) {
        showError('部分檔案無法加入', errors.join('\n'));
      }
    };

    const handleFileSelect = (event) => {
      addFiles(Array.from(event.target.files));
      event.target.value = '';
    };

    // 遞迴讀取 FileSystemEntry（支援資料夾）
    const readEntryFiles = (entry) => {
      return new Promise((resolve) => {
        if (entry.isFile) {
          entry.file(
            (file) => resolve([file]),
            () => resolve([])
          );
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          const allFiles = [];
          const readBatch = () => {
            reader.readEntries((entries) => {
              if (entries.length === 0) {
                resolve(allFiles);
                return;
              }
              Promise.all(entries.map(readEntryFiles)).then((results) => {
                results.forEach((files) => allFiles.push(...files));
                readBatch(); // 繼續讀取（readEntries 每次最多回傳 100 筆）
              });
            }, () => resolve(allFiles));
          };
          readBatch();
        } else {
          resolve([]);
        }
      });
    };

    const handleDrop = async (event) => {
      isDragging.value = false;

      const items = event.dataTransfer?.items;
      if (items && items.length > 0) {
        // 使用 FileSystem API 支援資料夾遞迴
        const entries = Array.from(items)
          .map((item) => item.webkitGetAsEntry?.())
          .filter(Boolean);

        const results = await Promise.all(entries.map(readEntryFiles));
        const files = results.flat();
        addFiles(files);
      } else {
        // 降級：直接使用 dataTransfer.files
        addFiles(Array.from(event.dataTransfer.files));
      }
    };

    const removeFile = (index) => {
      selectedFiles.value.splice(index, 1);
    };

    const clearAllFiles = () => {
      selectedFiles.value = selectedFiles.value.filter(item => item.status !== 'pending');
    };

    const retryFile = (index) => {
      const item = selectedFiles.value[index];
      if (item && item.status === 'error') {
        item.status = 'pending';
        item.errorMessage = '';
      }
    };

    const retryAllFailed = () => {
      selectedFiles.value.forEach(item => {
        if (item.status === 'error') {
          item.status = 'pending';
          item.errorMessage = '';
        }
      });
    };

    const handleUpload = async () => {
      const pendingFiles = selectedFiles.value.filter(item => item.status === 'pending');
      if (pendingFiles.length === 0) {
        await showError('上傳失敗', '請選擇檔案');
        return;
      }

      uploading.value = true;
      uploadedCount.value = 0;
      totalUploadCount.value = pendingFiles.length;

      const tags = tagsInput.value
        ? tagsInput.value.split(',').map(t => t.trim()).filter(t => t)
        : [];

      let successCount = 0;
      let failCount = 0;

      for (const item of pendingFiles) {
        item.status = 'uploading';
        try {
          const formData = new FormData();
          formData.append('file', item.file);
          formData.append('title', item.file.name.replace(/\.[^/.]+$/, ''));
          if (tags.length > 0) {
            formData.append('tags', JSON.stringify(tags));
          }

          const response = await chatmateService.uploadKnowledge(formData);
          if (response && response.success) {
            item.status = 'success';
            successCount++;
          } else {
            item.status = 'error';
            item.errorMessage = response?.message || '上傳失敗';
            failCount++;
          }
        } catch (err) {
          item.status = 'error';
          item.errorMessage = err.response?.data?.message || err.message || '上傳失敗';
          failCount++;
        }
        uploadedCount.value++;
      }

      uploading.value = false;

      if (failCount === 0) {
        await showSuccess('上傳完成', `${successCount} 個檔案已上傳，正在處理中`);
        router.push({ name: 'ChatMateKnowledge' });
      } else if (successCount === 0) {
        await showError('上傳失敗', `${failCount} 個檔案上傳失敗，請確認格式後重試`);
      } else {
        await showSuccess(
          '部分上傳完成',
          `成功 ${successCount} 個，失敗 ${failCount} 個。失敗的檔案請確認後重試。`
        );
      }
    };

    const formatFileSize = (bytes) => {
      if (!bytes) return '0 B';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const getFileIcon = (filename) => {
      const ext = filename.split('.').pop().toLowerCase();
      if (ext === 'pdf') return 'ri-file-pdf-line';
      if (ext === 'docx') return 'ri-file-word-line';
      if (ext === 'xlsx') return 'ri-file-excel-line';
      if (ext === 'md') return 'ri-markdown-line';
      return 'ri-file-text-line';
    };

    return {
      pageTitle,
      fileInput,
      selectedFiles,
      isDragging,
      uploading,
      uploadedCount,
      totalUploadCount,
      tagsInput,
      pendingCount,
      failedCount,
      handleFileSelect,
      handleDrop,
      removeFile,
      clearAllFiles,
      retryFile,
      retryAllFailed,
      handleUpload,
      formatFileSize,
      getFileIcon
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.upload-form {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
}

.required {
  color: #e74c3c;
}

.file-upload-area {
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 12px;
}

.file-upload-area:hover,
.file-upload-area.dragover {
  border-color: #007bff;
  background-color: #f0f8ff;
}

.upload-placeholder i {
  font-size: 48px;
  color: #999;
  margin-bottom: 16px;
}

.upload-placeholder p {
  margin: 8px 0;
  color: #666;
}

.upload-placeholder .hint {
  font-size: 14px;
  color: #999;
}

/* 已選檔案列表 */
.format-tips {
  background: #f0f7ff;
  border: 1px solid #cce0ff;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 24px;
  font-size: 13px;
  color: #444;
}

.format-tips-title {
  font-weight: 600;
  color: #1a6bbf;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.format-tips ul {
  margin: 0;
  padding-left: 18px;
  line-height: 1.8;
}

.format-tips li {
  color: #555;
}

.tip-highlight {
  color: #d68910;
  font-weight: 600;
}

.file-list {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
}

.file-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #555;
}

.header-failed {
  color: #e74c3c;
}

.file-list-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-retry-all {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid #f0ad4e;
  color: #d68910;
  font-size: 12px;
  cursor: pointer;
  padding: 3px 8px;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-retry-all:hover {
  background: #fff3cd;
}

.btn-clear-all {
  background: none;
  border: none;
  color: #999;
  font-size: 13px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: color 0.2s;
}

.btn-clear-all:hover {
  color: #e74c3c;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: white;
  border-radius: 6px;
  margin-bottom: 8px;
  border: 1px solid #eee;
  transition: border-color 0.2s;
}

.file-item:last-child {
  margin-bottom: 0;
}

.file-item.success {
  border-color: #d4edda;
  background: #f8fff9;
}

.file-item.error {
  border-color: #f5c6cb;
  background: #fff8f8;
}

.file-type-icon {
  font-size: 22px;
  color: #007bff;
  flex-shrink: 0;
}

.file-item-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  display: block;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: 12px;
  color: #999;
}

.file-item-status {
  flex-shrink: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 12px;
  font-weight: 500;
}

.status-badge.pending {
  background: #e9ecef;
  color: #666;
}

.status-badge.uploading {
  background: #cce5ff;
  color: #004085;
}

.status-badge.success {
  background: #d4edda;
  color: #155724;
}

.status-badge.error {
  background: #f8d7da;
  color: #721c24;
  cursor: help;
}

.spinning {
  display: inline-block;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.file-error-msg {
  display: block;
  font-size: 11px;
  color: #e74c3c;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.btn-retry {
  background: none;
  border: none;
  color: #f0ad4e;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s;
  flex-shrink: 0;
  font-size: 16px;
}

.btn-retry:hover {
  background: #fff3cd;
  color: #d68910;
}

.btn-remove {
  background: none;
  border: none;
  color: #bbb;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s;
  flex-shrink: 0;
  font-size: 16px;
}

.btn-remove:hover {
  background: #f5c6cb;
  color: #e74c3c;
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 32px;
}
</style>
